import { isValidElement, cloneElement } from "react";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { twMerge } from "tailwind-merge";

interface ControlCenterButtonProps {
  id: string;
  icon: React.ReactNode;
  text: string;
  ariaLabel: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  active: boolean;
  disabled?: boolean;
  buttonVariant?: "light" | "dark";
}

const BUTTON_VARIANT_STYLES = {
  light: {
    active: "border-primary-600 bg-secondary-600 dark:bg-secondary-900",
    inactive: "border-white/10",
    disabled: "opacity-50",
    enabled: "hover:border-primary-600 focus:border-secondary-400",
    contentStroke: "stroke-white/70",
    contentText: "text-white/70",
  },
  dark: {
    active:
      "border-primary-600 bg-secondary-700 dark:bg-secondary-900 dark:border-primary-500",
    inactive: "border-secondary-700 dark:border-white/10",
    disabled: "opacity-50",
    enabled:
      "hover:border-primary-600 focus:border-secondary-400 dark:hover:border-primary-500 dark:focus:border-secondary-500",
    contentStroke: "stroke-secondary-700",
    contentText: "text-secondary-700",
  },
};

export const ControlCenterButton = ({
  id,
  icon,
  text,
  ariaLabel,
  onClick,
  active,
  disabled = false,
  buttonVariant = "light",
}: ControlCenterButtonProps) => {
  const IconWithClass = isValidElement(icon)
    ? cloneElement(icon as React.ReactElement<{ className?: string }>, {
        className: active
          ? "stroke-white"
          : `${BUTTON_VARIANT_STYLES[buttonVariant].contentStroke} dark:stroke-white/70`,
      })
    : icon;

  return (
    <button
      id={id}
      onClick={onClick}
      className={twMerge(
        "flex w-full cursor-pointer flex-col items-center gap-1 rounded-2xl border px-3 py-3.5 outline-none",
        active
          ? BUTTON_VARIANT_STYLES[buttonVariant].active
          : BUTTON_VARIANT_STYLES[buttonVariant].inactive,
        disabled
          ? BUTTON_VARIANT_STYLES[buttonVariant].disabled
          : BUTTON_VARIANT_STYLES[buttonVariant].enabled
      )}
      data-testid={id}
      aria-label={ariaLabel}
      disabled={disabled}
    >
      {IconWithClass}
      <Typography
        className={twMerge(
          "leading-[120%] font-medium",
          active
            ? "text-white"
            : `${BUTTON_VARIANT_STYLES[buttonVariant].contentText} dark:text-white/70`
        )}
        size="xs"
      >
        {text}
      </Typography>
    </button>
  );
};
